// Carer modal — POST /carers in create mode, PATCH /carers/:id in edit
// mode (TASK-N48; backend gap closed by TASK-N26 at 5a650a6, expanded
// updateCarerSchema covers the full editable field set).

import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { JSX } from "react";
import { apiFetch } from "../lib/api.js";
import { Modal } from "./Modal.js";
import { FormField } from "./FormField.js";
import { SelectField } from "./SelectField.js";
import { PhoneField } from "./PhoneField.js";
import { SwitchToggle } from "./SwitchToggle.js";
import { PincodeTagInput } from "./PincodeTagInput.js";
import { CheckboxGroupWithOther } from "./CheckboxGroupWithOther.js";
import {
  GENDER_OPTIONS, QUAL_OPTIONS, TIER_OPTIONS, SKILL_OPTIONS, ONBOARDING_ITEMS,
  INIT, validate, buildCarerPayload, type FormState, type FormErrors,
} from "./NewCarerModal.sections.js";

type Mode = "create" | "edit";

interface Props {
  onClose: () => void;
  onCreated: () => void;
  mode?: Mode;
  // In edit mode the caller pre-fetches the full carer payload and passes
  // it here. The modal does not fetch on its own — keeps the modal focused
  // on form mechanics and lets the caller surface loading/error UX.
  initialValues?: Partial<FormState>;
  carerId?: string;
}
interface Agency { _id: string; agency_name: string; }

function submitLabel(pending: boolean, isEdit: boolean): string {
  if (pending) return isEdit ? "Saving…" : "Creating…";
  return isEdit ? "Save Changes" : "Create Carer";
}

// eslint-disable-next-line max-lines-per-function, complexity -- coherent multi-step modal; per-step extraction would shuffle lines without semantic gain (sub-sections already in NewCarerModal.sections.tsx). Tracked under TASK-N19 Phase B.
export function NewCarerModal({ onClose, onCreated, mode = "create", initialValues, carerId }: Props): JSX.Element {
  const isEdit = mode === "edit";
  const [form, setForm] = useState<FormState>({ ...INIT, ...(initialValues ?? {}) });
  const [errors, setErrors] = useState<FormErrors>({});
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoUploading, setPhotoUploading] = useState(false);

  const { data: agencyData } = useQuery<{ data: Agency[]; total: number; page: number; limit: number }>({
    queryKey: ["agencies"],
    queryFn: () => apiFetch<{ data: Agency[]; total: number; page: number; limit: number }>("/agencies"),
  });

  const on = (patch: Partial<FormState>): void => setForm((p) => ({ ...p, ...patch }));
  const setArr = (field: keyof FormState) => (val: string): void =>
    on({ [field]: val.split(",").map((s) => s.trim()).filter(Boolean) } as Partial<FormState>);

  async function handlePhotoChange(file: File): Promise<void> {
    setPhotoUploading(true);
    setPhotoPreview(URL.createObjectURL(file));
    try {
      const { upload_url, file_key } = await apiFetch<{ upload_url: string; file_key: string }>(
        "/uploads/presign",
        { method: "POST", body: JSON.stringify({ file_name: file.name, content_type: file.type, resource_type: "carer_photo" }) },
      );
      await fetch(upload_url, { method: "PUT", body: file, headers: { "Content-Type": file.type } });
      on({ photo_url: file_key });
    } catch { setPhotoPreview(null); }
    finally { setPhotoUploading(false); }
  }

  const mutation = useMutation({
    mutationFn: () => {
      const url = isEdit && carerId ? `/carers/${carerId}` : "/carers";
      const method = isEdit ? "PATCH" : "POST";
      return apiFetch<unknown>(url, { method, body: JSON.stringify(buildCarerPayload(form)) });
    },
    onSuccess: onCreated,
  });

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    mutation.mutate();
  }

  const agencyOpts = [
    { value: "", label: agencyData ? "— none —" : "Loading…" },
    ...(agencyData?.data ?? []).map((a) => ({ value: a._id, label: a.agency_name })),
  ];

  return (
    <Modal title={isEdit ? "Edit Carer" : "New Carer"} onClose={onClose}>
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <FormField label="Full name" value={form.full_name} onChange={(v) => on({ full_name: v })} required error={errors.full_name} />
        <PhoneField label="Phone" value={form.primary_phone} onChange={(v) => on({ primary_phone: v })} required error={errors.primary_phone} />
        <FormField label="City" value={form.city} onChange={(v) => on({ city: v })} />
        <SelectField label="Gender" value={form.gender} onChange={(v) => on({ gender: v })} options={GENDER_OPTIONS} />
        <FormField label="Languages spoken" value={form.languages_spoken.join(", ")} onChange={setArr("languages_spoken")} placeholder="e.g. Hindi, Tamil, English" />
        <SelectField label="Primary qualification" value={form.qualifications[0] ?? ""} onChange={(v) => on({ qualifications: v ? [v] : [] })} options={[{ value: "", label: "— select —" }, ...QUAL_OPTIONS]} />
        <FormField label="Training completed" value={form.training_completed.join(", ")} onChange={setArr("training_completed")} placeholder="e.g. dementia_care_training, basic_life_support" />
        <FormField label="Specialist skills" value={form.specialist_skills.join(", ")} onChange={setArr("specialist_skills")} placeholder="e.g. insulin_administration, wound_care" />
        <SelectField label="Tier" value={form.tier} onChange={(v) => on({ tier: v })} options={TIER_OPTIONS} />
        <div>
          <p className="block text-sm font-medium text-slate-700 mb-2">Skills <span className="text-red-500">*</span></p>
          <CheckboxGroupWithOther
            options={SKILL_OPTIONS as unknown as { value: string; label: string }[]}
            value={form.skills} onChange={(v) => on({ skills: v })}
            otherPlaceholder="e.g. Wound care, Palliative care…" error={errors.skills}
          />
        </div>

        {/* ── New fields ── */}
        <PincodeTagInput value={form.coverage_pincodes} onChange={(v) => on({ coverage_pincodes: v })} />
        <FormField label="Years experience" type="number" value={form.years_experience} onChange={(v) => on({ years_experience: v })} placeholder="0–40" />

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Photo</label>
          <input
            type="file" accept="image/jpeg,image/png,image/webp"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void handlePhotoChange(f); }}
            className="text-sm text-slate-600"
          />
          {photoUploading && <p className="text-xs text-slate-400 mt-1">Uploading…</p>}
          {photoPreview && !photoUploading && (
            <img src={photoPreview} alt="Preview" className="mt-2 h-16 w-16 rounded-full object-cover border border-slate-200" />
          )}
        </div>

        <SwitchToggle label="ID verified" checked={form.id_verified} onChange={(v) => on({ id_verified: v, background_verified_date: v ? form.background_verified_date : "" })} />
        {form.id_verified && (
          <FormField label="Background verified date" type="date" value={form.background_verified_date} onChange={(v) => on({ background_verified_date: v })} required error={errors.background_verified_date} />
        )}
        <FormField label="Contract expiry date" type="date" value={form.contract_expiry_date} onChange={(v) => on({ contract_expiry_date: v })} />

        <div className="border border-slate-200 rounded-lg p-3 space-y-2">
          <p className="text-sm font-medium text-slate-700">Onboarding checklist</p>
          {ONBOARDING_ITEMS.map(({ key, label }) => (
            <label key={key} className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                checked={form.onboarding_checklist.includes(key)}
                onChange={(e) => on({ onboarding_checklist: e.target.checked ? [...form.onboarding_checklist, key] : form.onboarding_checklist.filter((k) => k !== key) })}
                className="rounded text-navy-700"
              />
              {label}
            </label>
          ))}
        </div>

        <SelectField label="Agency" value={form.agency_id} onChange={(v) => on({ agency_id: v })} options={agencyOpts} />

        {mutation.isError && (
          <p role="alert" className="text-sm text-red-600">
            {isEdit ? "Failed to save changes. Please try again." : "Failed to create carer. Please try again."}
          </p>
        )}
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="text-sm text-slate-600 px-4 py-2 rounded-lg hover:bg-slate-100 transition-colors">
            Cancel
          </button>
          <button
            type="submit" disabled={mutation.isPending || photoUploading}
            className="bg-navy-700 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-navy-800 disabled:opacity-40 transition-colors"
          >
            {submitLabel(mutation.isPending, isEdit)}
          </button>
        </div>
      </form>
    </Modal>
  );
}
